/**
 * /cricket/api-tokens — issue + manage Bearer tokens for the public API.
 *
 * Tokens are shown ONCE on creation. After that, only the prefix is visible.
 */
import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Plus, Trash2, Copy, Key, ExternalLink, Sparkles } from "lucide-react";
import api from "../../api/axios";
import useDialogDismiss from "../../hooks/useDialogDismiss";

const SCOPES = [
  { v: "*",                 l: "All read access (full)" },
  { v: "read:matches",      l: "Matches list + detail" },
  { v: "read:balls",        l: "Ball-by-ball" },
  { v: "read:players",      l: "Players + stats" },
  { v: "feed:provisional",  l: "Live feed — provisional (premium, betting-grade)" },
  { v: "feed:confirmed",    l: "Live feed — confirmed consensus" },
  { v: "feed:certified",    l: "Live feed — certified post-match" },
  { v: "read:live",         l: "Live feed (legacy alias for feed:confirmed)" },
  { v: "read:historical",   l: "Historical aggregations" },
];

export default function ApiTokens() {
  const [params, setParams] = useSearchParams();
  const showWelcome = params.get("welcome") === "1";
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [newToken, setNewToken] = useState(null);
  const [form, setForm] = useState({ name: "", scopes: ["*"], rate_limit_per_minute: 60 });
  const createPanelRef = useRef(null);
  const tokenPanelRef = useRef(null);
  useDialogDismiss({
    open: createOpen && !newToken,
    onClose: () => setCreateOpen(false),
    ref: createPanelRef,
  });
  useDialogDismiss({
    open: !!newToken,
    onClose: () => { setNewToken(null); setCreateOpen(false); },
    ref: tokenPanelRef,
  });

  const dismissWelcome = () => {
    const next = new URLSearchParams(params);
    next.delete("welcome");
    setParams(next, { replace: true });
  };

  const reload = async () => {
    setLoading(true);
    try {
      const r = await api.get("/api/v1/tokens/");
      setRows(r.data?.results || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { reload(); }, []);

  const create = async () => {
    if (!form.name.trim()) return;
    const r = await api.post("/api/v1/tokens/", form);
    setNewToken(r.data);
    setForm({ name: "", scopes: ["*"], rate_limit_per_minute: 60 });
    reload();
  };
  const revoke = async (id) => {
    if (!confirm("Revoke this token? Active integrations will stop working immediately.")) return;
    await api.delete(`/api/v1/tokens/${id}/`);
    reload();
  };
  const copy = (text) => {
    navigator.clipboard.writeText(text);
  };

  return (
    <div className="p-4 sm:p-6 max-w-4xl mx-auto">
      <div className="flex items-start justify-between mb-5">
        <div>
          <h2 className="text-xl font-bold flex items-center gap-2">
            <Key className="w-5 h-5" /> API tokens
          </h2>
          <p className="text-sm text-text-secondary mt-1">
            Issue Bearer tokens for programmatic access to the public API.
          </p>
          <div className="flex items-center gap-3 mt-1">
            <a
              href="/api/v1/docs/"
              target="_blank" rel="noreferrer"
              className="text-xs text-button-primary hover:underline inline-flex items-center gap-1"
            >
              View Swagger docs <ExternalLink className="w-3 h-3" />
            </a>
            <a
              href="/zencricketdata.postman_collection.json"
              download
              className="text-xs text-button-primary hover:underline inline-flex items-center gap-1"
            >
              Download Postman collection
            </a>
          </div>
        </div>
        <button
          onClick={() => { setCreateOpen(true); setNewToken(null); }}
          className="bg-button-primary text-white px-3 py-2 rounded-md text-sm flex items-center gap-1.5"
        >
          <Plus className="w-4 h-4" /> New token
        </button>
      </div>

      {showWelcome && (
        <div className="mb-5 rounded-xl border border-button-primary/40 bg-gradient-to-r from-button-primary/15 to-button-primary/5 p-4 flex items-start gap-3">
          <Sparkles className="w-5 h-5 text-button-primary mt-0.5 shrink-0" />
          <div className="flex-1">
            <div className="font-semibold">Welcome aboard! Your account is verified.</div>
            <p className="text-sm text-text-secondary mt-1">
              Mint your first API token below to start pulling matches, players, and live ball-by-ball data.
              Tokens are shown only once — copy carefully.
            </p>
          </div>
          <button onClick={dismissWelcome} className="text-text-secondary hover:text-text-primary text-sm">
            Dismiss
          </button>
        </div>
      )}

      {/* Token list */}
      <div className="bg-bg-primary border border-border-default rounded-xl overflow-x-auto">
        <table className="w-full text-sm whitespace-nowrap">
          <thead className="bg-bg-secondary text-text-secondary text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left py-2 px-3">Name</th>
              <th className="text-left py-2 px-3">Prefix</th>
              <th className="text-left py-2 px-3">Scopes</th>
              <th className="text-left py-2 px-3">Rate</th>
              <th className="text-left py-2 px-3">Last used</th>
              <th className="text-left py-2 px-3">State</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="py-6 text-center text-text-secondary">Loading…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={7} className="py-6 text-center text-text-secondary">No tokens issued yet.</td></tr>
            ) : rows.map((t) => (
              <tr key={t.id} className="border-t border-border-default">
                <td className="py-2 px-3">{t.name}</td>
                <td className="py-2 px-3 font-mono text-xs">{t.token_prefix}…</td>
                <td className="py-2 px-3">
                  {(t.scopes || []).map((s) => (
                    <span key={s} className="text-[10px] px-1.5 py-0.5 mr-1 rounded bg-bg-secondary border border-border-default">{s}</span>
                  ))}
                </td>
                <td className="py-2 px-3 text-text-secondary">{t.rate_limit_per_minute}/min</td>
                <td className="py-2 px-3 text-text-secondary text-xs">
                  {t.last_used_at ? new Date(t.last_used_at).toLocaleString() : "—"}
                </td>
                <td className="py-2 px-3">
                  {t.is_active && !t.revoked_at ? (
                    <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">Active</span>
                  ) : (
                    <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-red-500/15 text-red-300 border border-red-500/30">Revoked</span>
                  )}
                </td>
                <td className="py-2 px-3">
                  {t.is_active && !t.revoked_at && (
                    <button onClick={() => revoke(t.id)} className="text-text-secondary hover:text-red-400">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Create modal */}
      {createOpen && !newToken && (
        <div
          className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          role="presentation"
          onClick={() => setCreateOpen(false)}
        >
          <div
            ref={createPanelRef}
            role="dialog"
            aria-modal="true"
            aria-label="Issue new API token"
            className="bg-bg-primary rounded-2xl max-w-md w-full border border-bg-secondary shadow-2xl p-5"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-base font-semibold mb-3">Issue new API token</h3>
            <div className="space-y-3 text-sm">
              <label className="flex flex-col gap-1">
                <span className="text-xs uppercase tracking-wider text-text-secondary">Name</span>
                <input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g. Broadcaster integration"
                  className="bg-bg-secondary px-3 py-2 rounded text-text-primary"
                />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-xs uppercase tracking-wider text-text-secondary">Scopes</span>
                <select
                  multiple
                  value={form.scopes}
                  onChange={(e) => setForm({ ...form, scopes: Array.from(e.target.selectedOptions, (o) => o.value) })}
                  className="bg-bg-secondary px-3 py-2 rounded text-text-primary"
                  size={SCOPES.length}
                >
                  {SCOPES.map((s) => (
                    <option key={s.v} value={s.v}>{s.l}</option>
                  ))}
                </select>
                <span className="text-[10px] text-text-secondary">Cmd / Ctrl-click for multiple. "*" overrides everything.</span>
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-xs uppercase tracking-wider text-text-secondary">Rate limit (req/min)</span>
                <input
                  type="number" min={10} max={1000}
                  value={form.rate_limit_per_minute}
                  onChange={(e) => setForm({ ...form, rate_limit_per_minute: Number(e.target.value) })}
                  className="bg-bg-secondary px-3 py-2 rounded text-text-primary"
                />
              </label>
            </div>
            <div className="flex gap-2 justify-end pt-4">
              <button onClick={() => setCreateOpen(false)} className="px-3 py-1.5 rounded bg-bg-secondary text-text-secondary">Cancel</button>
              <button onClick={create} disabled={!form.name.trim()} className="px-3 py-1.5 rounded bg-button-primary text-white disabled:opacity-50">
                Issue
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Token reveal modal — shown ONCE */}
      {newToken && (
        <div
          className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          role="presentation"
          onClick={(e) => { if (e.target === e.currentTarget) { setNewToken(null); setCreateOpen(false); } }}
        >
          <div
            ref={tokenPanelRef}
            role="dialog"
            aria-modal="true"
            aria-label="New API token"
            className="bg-bg-primary rounded-2xl max-w-md w-full border border-amber-500/40 shadow-2xl p-5"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-base font-semibold mb-2 text-amber-400">⚠ Copy this token now</h3>
            <p className="text-xs text-text-secondary mb-3">
              This is the only time you'll see the full token. Store it securely.
              Revoking + re-issuing is the only way to rotate.
            </p>
            <div className="bg-bg-secondary rounded p-3 font-mono text-[11px] text-text-primary break-all flex items-start gap-2">
              <span className="flex-1">{newToken.token}</span>
              <button onClick={() => copy(newToken.token)} className="shrink-0 text-text-secondary hover:text-text-primary" title="Copy to clipboard">
                <Copy className="w-4 h-4" />
              </button>
            </div>
            <pre className="text-[10px] text-text-secondary mt-3 bg-bg-secondary/40 rounded p-2 overflow-x-auto">
{`curl -H "Authorization: Bearer ${newToken.token.slice(0, 20)}…" \\
  https://api.zencricketdata.com/api/v1/matches/`}
            </pre>
            <div className="flex gap-2 justify-end pt-4">
              <button onClick={() => { setNewToken(null); setCreateOpen(false); }} className="px-3 py-1.5 rounded bg-button-primary text-white">
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
